import React from "react";
import { useSearchParams } from "react-router-dom";
import PrintableSheet from "./PrintableSheet";
import { colors } from "../../config/site";
import { CheckCircle2, AlertTriangle, Wallet } from "lucide-react";

const currency = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

/**
 * Print-friendly Move-In Reality Check plan.
 * Reads all input + calculated values from URL query params so nothing needs
 * to be persisted server-side. Uses the shared PrintableSheet wrapper so it
 * matches the branded look of every other printable resource.
 */
const MoveInPlan = () => {
  const [params] = useSearchParams();

  const rent = Number(params.get("rent") || 0);
  const savings = Number(params.get("savings") || 0);
  const monthly = Number(params.get("monthly") || 0);
  const depositMonths = Number(params.get("dep") || 1);
  const furniture = params.get("furn") || "some";
  const moving = params.get("move") || "diy";

  const securityDeposit = rent * depositMonths;
  const firstMonthRent = rent;
  const applicationFee = rent > 0 ? 50 : 0;
  const utilitySetup = rent > 0 ? 200 : 0;
  const rentersInsurance = rent > 0 ? 45 : 0;
  const movingCost =
    moving === "movers" ? 800 : moving === "help" ? 200 : rent > 0 ? 75 : 0;
  const furnitureCost =
    furniture === "most" ? 1500 : furniture === "some" ? 500 : 0;

  const total =
    securityDeposit +
    firstMonthRent +
    utilitySetup +
    rentersInsurance +
    movingCost +
    furnitureCost +
    applicationFee;

  const gap = Math.max(0, total - savings);
  const monthsToReady = gap > 0 && monthly > 0 ? Math.ceil(gap / monthly) : 0;
  const isReady = total > 0 && savings >= total;

  const furnitureLabel =
    furniture === "most"
      ? "Starting from scratch"
      : furniture === "some"
      ? "A few basics"
      : "Already have it";
  const movingLabel =
    moving === "movers"
      ? "Hired movers"
      : moving === "help"
      ? "Rental truck"
      : "DIY / friends";

  const lineItems = [
    {
      label: "Security deposit",
      value: securityDeposit,
      note: `${depositMonths} × monthly rent`,
    },
    { label: "First month's rent", value: firstMonthRent, note: "Paid at signing" },
    {
      label: "Application & credit-check fees",
      value: applicationFee,
      note: "Typically $35–$75, non-refundable",
    },
    {
      label: "Utility setup deposits",
      value: utilitySetup,
      note: "Electric, gas, internet",
    },
    {
      label: "Renters insurance (first payment)",
      value: rentersInsurance,
      note: "Usually required in the lease",
    },
    { label: "Moving costs", value: movingCost, note: movingLabel },
    {
      label: "Furniture & essentials",
      value: furnitureCost,
      note: furnitureLabel,
    },
  ];

  return (
    <PrintableSheet
      eyebrow="MODULE 2 · MONEY MATTERS"
      title="My Move-In Plan"
      subtitle="A snapshot of your upfront cost, current savings, and runway to move-in ready."
      backHref="/tools/move-in-reality-check"
      backLabel="Back to the Reality Check"
      testId="printable-move-in-plan"
    >
      {/* Snapshot header */}
      <div
        className="rounded-2xl p-5 mb-6 grid sm:grid-cols-3 gap-4"
        style={{ backgroundColor: colors.ivory }}
      >
        <div>
          <p
            className="text-xs uppercase tracking-widest font-bold mb-1"
            style={{ color: colors.textMuted }}
          >
            Target monthly rent
          </p>
          <p className="text-2xl font-bold" style={{ color: colors.charcoal }}>
            {currency(rent)}
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-widest font-bold mb-1"
            style={{ color: colors.textMuted }}
          >
            Currently saved
          </p>
          <p className="text-2xl font-bold" style={{ color: colors.charcoal }}>
            {currency(savings)}
          </p>
        </div>
        <div>
          <p
            className="text-xs uppercase tracking-widest font-bold mb-1"
            style={{ color: colors.textMuted }}
          >
            Saving per month
          </p>
          <p className="text-2xl font-bold" style={{ color: colors.charcoal }}>
            {currency(monthly)}
          </p>
        </div>
      </div>

      {/* Cost breakdown */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Wallet className="w-5 h-5" style={{ color: colors.terracotta }} />
          <p
            className="text-xs uppercase tracking-widest font-bold"
            style={{ color: colors.terracottaDark }}
          >
            What move-in actually costs
          </p>
        </div>
        <div
          className="rounded-2xl p-5"
          style={{
            backgroundColor: colors.white,
            border: `1px solid #EFE9DE`,
          }}
        >
          {lineItems.map((item) => (
            <div
              key={item.label}
              className="flex justify-between items-start py-2.5 border-b last:border-b-0"
              style={{ borderColor: "#EFE9DE" }}
            >
              <div className="pr-3">
                <p
                  className="text-sm font-semibold"
                  style={{ color: colors.charcoal }}
                >
                  {item.label}
                </p>
                <p
                  className="text-xs mt-0.5"
                  style={{ color: colors.textMuted }}
                >
                  {item.note}
                </p>
              </div>
              <p
                className="text-sm font-bold whitespace-nowrap"
                style={{ color: colors.charcoal }}
              >
                {currency(item.value)}
              </p>
            </div>
          ))}
          <div className="flex justify-between items-center pt-4 mt-2">
            <p
              className="text-base font-bold"
              style={{ color: colors.charcoal }}
            >
              Total needed before you sign
            </p>
            <p
              className="text-2xl font-bold"
              style={{ color: colors.primary }}
              data-testid="plan-total"
            >
              {currency(total)}
            </p>
          </div>
        </div>
      </div>

      {/* Runway card */}
      <div
        className="rounded-2xl p-5 mb-6"
        style={{
          backgroundColor: isReady
            ? colors.sage + "1a"
            : colors.terracotta + "15",
          borderLeft: `4px solid ${isReady ? colors.primary : colors.terracotta}`,
        }}
      >
        <div className="flex items-start gap-3 mb-3">
          {isReady ? (
            <CheckCircle2
              className="w-6 h-6 flex-shrink-0 mt-0.5"
              style={{ color: colors.primary }}
            />
          ) : (
            <AlertTriangle
              className="w-6 h-6 flex-shrink-0 mt-0.5"
              style={{ color: colors.terracotta }}
            />
          )}
          <div>
            <p
              className="text-xs uppercase tracking-widest font-bold mb-1"
              style={{
                color: isReady ? colors.primary : colors.terracottaDark,
              }}
            >
              {isReady ? "You're move-in ready" : "Your runway"}
            </p>
            <p
              className="text-lg font-bold"
              style={{ color: colors.charcoal }}
            >
              {isReady
                ? "Your savings cover every upfront cost."
                : monthsToReady
                ? `At ${currency(monthly)}/mo saved, about ${monthsToReady} month${
                    monthsToReady === 1 ? "" : "s"
                  } from ready.`
                : "Add a monthly savings amount to see your runway."}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <p
              className="text-xs uppercase tracking-widest font-bold mb-1"
              style={{ color: colors.textMuted }}
            >
              Gap remaining
            </p>
            <p
              className="text-xl font-bold"
              style={{
                color: gap > 0 ? colors.terracottaDark : colors.primary,
              }}
            >
              {currency(gap)}
            </p>
          </div>
          <div>
            <p
              className="text-xs uppercase tracking-widest font-bold mb-1"
              style={{ color: colors.textMuted }}
            >
              Months to ready
            </p>
            <p
              className="text-xl font-bold"
              style={{ color: colors.charcoal }}
            >
              {isReady ? "Now" : monthsToReady ? `${monthsToReady} mo` : "—"}
            </p>
          </div>
        </div>
      </div>

      {/* My next steps */}
      <div>
        <p
          className="text-xs uppercase tracking-widest font-bold mb-3"
          style={{ color: colors.sage }}
        >
          My next steps
        </p>
        <ul className="space-y-2.5">
          {[
            gap > 0
              ? `Set aside ${currency(monthly || 100)} every month toward the move-in fund.`
              : "Keep at least one month of expenses saved as an emergency cushion.",
            "Run the Monthly Budget Calculator to confirm this rent fits long-term.",
            "Ask the landlord for the full lease and any move-in fees in writing before signing.",
            "Line up utility accounts (electric, gas, internet) 2 weeks before move-in.",
          ].map((step, i) => (
            <li
              key={i}
              className="flex items-start gap-3 p-3 rounded-xl"
              style={{ backgroundColor: colors.cream }}
            >
              <div
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: colors.primary }}
              >
                {i + 1}
              </div>
              <p
                className="text-sm pt-0.5"
                style={{ color: colors.charcoal }}
              >
                {step}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <p
        className="text-xs mt-8 italic"
        style={{ color: colors.textMuted }}
      >
        Estimates based on typical Pennsylvania move-in costs. Actual amounts vary
        by landlord, unit, and provider. Educational only — not legal or financial advice.
      </p>
    </PrintableSheet>
  );
};

export default MoveInPlan;
